<script setup lang="ts">
  defineOptions({ name: 'ProceedToLogin' })

  // Vue core imports
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  // Internal imports
  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
  import CPButton from '@ui/components/CPButton.vue'
  import CPText from '@ui/components/CPText.vue'

  import { createElementId } from '../../../core-layer/utils'
  import { Screens } from '../../types/auth'
  import { requestAuthenticatedLoginVisit } from '../../utils/authenticated-login-visit'

  // Props definitions
  const props = defineProps({
    email: {
      type: String,
      default: '',
    },
    /** Override the default message. When undefined, uses auth.proceedToLogin.message. Pass '' to hide. */
    message: {
      type: String,
      default: undefined,
    },
    /** When true, navigates to auth-login page instead of emitting changeScreen (for standalone pages) */
    navigateToLoginOnClick: {
      type: Boolean,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: { page: Screens; email?: string }): void
    (e: 'loginCallback', value: unknown): void
  }>()

  const { t } = useI18n()
  const { CPNavigateTo } = useCPNavigation()

  const displayMessage = computed(() => (props.message !== undefined ? props.message : t('auth.proceedToLogin.message')))

  async function handleClick() {
    if (props.navigateToLoginOnClick) {
      // The user may still have an active session for a different account (e.g. they
      // were logged in as user A and just completed a forgot-password flow for user B).
      // Without this one-shot bypass, the auth middleware would bounce them straight
      // to /dashboard. Consumed by the next middleware run on /login.
      requestAuthenticatedLoginVisit()
      await CPNavigateTo({ name: 'auth-login' })
    } else {
      history.pushState({}, '', '')
      emit('changeScreen', {
        page: Screens.LOGIN,
        email: props.email,
      })
    }
  }
</script>

<template>
  <div class="flex w-full flex-col items-center">
    <CPText v-if="displayMessage" size="md" class="text-center">
      {{ displayMessage }}
    </CPText>

    <CPButton
      :id="createElementId('proceed-to-login', 'modal', 'login-button')"
      :class="displayMessage ? 'mt-3 w-full' : 'w-full'"
      type="primary"
      size="md"
      @click="handleClick"
    >
      {{ t('auth.proceedToLogin.button') }}
    </CPButton>
  </div>
</template>

<style scoped>
  .centered {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
</style>
